const TRANSFER_FEE_RATE = 0.02
const SBT_RATE = 0.033
const STAMP_DUTY_RATE = 0.005
const WITHHOLDING_JURISTIC_RATE = 0.01
// Revenue Dept. caps the years used to divide/annualize PIT for individual sellers at 10,
// even though holding years (and the deduction % table) can go higher.
const MAX_PIT_YEARS = 10

// Revenue Department deduction-by-holding-year table for individual sellers
const INDIVIDUAL_DEDUCTION_TABLE = {
  1: 0.92,
  2: 0.84,
  3: 0.77,
  4: 0.71,
  5: 0.65,
  6: 0.60,
  7: 0.55,
  8: 0.50,
}

// Land Department withholding-tax-on-sale schedule for individuals - distinct from
// the annual PIT filing schedule: no 0% starting bracket, and 35% kicks in at 4,000,000.
const PIT_BRACKETS = [
  { upTo: 300000, rate: 0.05 },
  { upTo: 500000, rate: 0.10 },
  { upTo: 750000, rate: 0.15 },
  { upTo: 1000000, rate: 0.20 },
  { upTo: 2000000, rate: 0.25 },
  { upTo: 4000000, rate: 0.30 },
  { upTo: Infinity, rate: 0.35 },
]

// Specific Business Tax's 5-year threshold is counted day-to-day (นับวันชนวัน),
// i.e. actual completed years elapsed - not calendar years touched.
export function getHoldingYears(dateAcquired, dateSold = new Date()) {
  if (!dateAcquired) return 0

  const acquired = new Date(dateAcquired)
  const sold = new Date(dateSold)

  if (Number.isNaN(acquired.getTime()) || Number.isNaN(sold.getTime())) return 0

  let years = sold.getFullYear() - acquired.getFullYear()

  if (
    sold.getMonth() < acquired.getMonth() ||
    (sold.getMonth() === acquired.getMonth() && sold.getDate() < acquired.getDate())
  ) {
    years -= 1
  }

  return Math.max(years, 0)
}

// Individual PIT withholding (deduction table + bracket divisor) is counted by calendar
// year instead: transfer year minus acquisition year, with any partial year rounded up to 1.
export function getCalendarHoldingYears(dateAcquired, dateSold = new Date()) {
  if (!dateAcquired) return 1

  const acquired = new Date(dateAcquired)
  const sold = new Date(dateSold)

  if (Number.isNaN(acquired.getTime()) || Number.isNaN(sold.getTime())) return 1

  const years = sold.getFullYear() - acquired.getFullYear() + 1

  return Math.max(years, 1)
}

export function calcTransferFee(appraisedValue) {
  return Math.max(appraisedValue, 0) * TRANSFER_FEE_RATE
}

export function calcSbtOrStampDuty({ salePrice, appraisedValue, holdingYears, isExempt }) {
  const base = Math.max(salePrice || 0, appraisedValue || 0)

  if (holdingYears < 5 && !isExempt) {
    return { type: 'sbt', rate: SBT_RATE, base, amount: base * SBT_RATE }
  }

  return { type: 'stamp', rate: STAMP_DUTY_RATE, base, amount: base * STAMP_DUTY_RATE }
}

function applyPitBrackets(amount) {
  let remaining = amount
  let tax = 0
  let lowerBound = 0

  for (const bracket of PIT_BRACKETS) {
    if (remaining <= 0) break

    const bracketSize = bracket.upTo - lowerBound
    const taxableInBracket = Math.min(remaining, bracketSize)

    tax += taxableInBracket * bracket.rate
    remaining -= taxableInBracket
    lowerBound = bracket.upTo
  }

  return tax
}

export function calcWithholdingTaxJuristic({ salePrice, appraisedValue }) {
  const base = Math.max(salePrice || 0, appraisedValue || 0)
  return base * WITHHOLDING_JURISTIC_RATE
}

export function getCalendarYearForTax(calendarHoldingYears) {
  return Math.min(Math.max(calendarHoldingYears || 1, 1), MAX_PIT_YEARS)
}

export function calcWithholdingTaxIndividual({ appraisedValue, calendarHoldingYears }) {
  const years = Math.max(calendarHoldingYears || 1, 1)
  const deductionYears = Math.min(years, 8)
  const deductionPct = INDIVIDUAL_DEDUCTION_TABLE[deductionYears]
  const taxYears = getCalendarYearForTax(years)

  const netValue = Math.max(appraisedValue || 0, 0) * (1 - deductionPct)
  const perYear = netValue / taxYears
  const taxPerYear = applyPitBrackets(perYear)

  return taxPerYear * taxYears
}

export function calculateCondoTax({
  salePrice,
  appraisedValue,
  dateAcquired,
  transferDate,
  sellerType,
  isPrimaryResidenceExempt,
}) {
  const effectiveTransferDate = transferDate || new Date()
  const holdingYears = getHoldingYears(dateAcquired, effectiveTransferDate)
  const calendarHoldingYears = getCalendarHoldingYears(dateAcquired, effectiveTransferDate)

  const transferFee = calcTransferFee(appraisedValue)
  const sbtOrStampDuty = calcSbtOrStampDuty({
    salePrice,
    appraisedValue,
    holdingYears,
    isExempt: sellerType === 'individual' && isPrimaryResidenceExempt,
  })

  const withholdingTax = sellerType === 'juristic'
    ? calcWithholdingTaxJuristic({ salePrice, appraisedValue })
    : calcWithholdingTaxIndividual({ appraisedValue, calendarHoldingYears })

  const total = transferFee + sbtOrStampDuty.amount + withholdingTax
  const calendarYear = sellerType === 'individual' ? getCalendarYearForTax(calendarHoldingYears) : calendarHoldingYears

  return {
    holdingYears,
    calendarYear,
    transferFee,
    sbtOrStampDuty,
    withholdingTax,
    total,
  }
}
